import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
    LayoutDashboard,
    Palette,
    Sparkles,
    Calendar,
    BarChart3,
    TrendingUp,
    Lightbulb,
    Zap,
    Users,
    FolderOpen,
    Sun,
    Moon,
    Menu,
    X,
    MessageSquare,
    ChevronDown,
    ChevronRight,
    Bell,
    Settings,
    Search,
    Target,
    Layers,
    PieChart,
    Store,
    GitBranch,
    Compass,
    LogOut,
    User,
    Camera,
} from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getInitials } from '../utils/helpers';
import SidebarNotifications from './SidebarNotifications';

const Navigation = () => {
    const {
        user,
        theme,
        toggleTheme,
        brands,
        selectedBrand,
        setSelectedBrand,
        creditBalance,
        subscriptionStatus,
        formatCredits,
        creditsToUSD
    } = useApp();
    const { logout } = useAuth();
    const { unreadCount } = useNotifications();
    const navigate = useNavigate();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
    const [isWarRoomOpen, setIsWarRoomOpen] = useState(false);
    const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

    const handleSignOut = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const navItems = [
        { path: '/strategy', icon: Compass, label: 'Strategy Hub' },
        { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
        { path: '/brands', icon: Palette, label: 'Brands' },
        { path: '/generate', icon: Sparkles, label: 'Generate' },
        { path: '/photo-studio', icon: Camera, label: 'Photo Studio' },
        { path: '/creative-chat', icon: MessageSquare, label: 'Creative Chat' },
        { path: '/notifications', icon: Bell, label: 'Notifications', badge: unreadCount > 0 ? unreadCount : null },
        { path: '/schedule', icon: Calendar, label: 'Schedule' },
        { path: '/library', icon: FolderOpen, label: 'Library' },
        { path: '/analytics', icon: BarChart3, label: 'Analytics' },
        {
            icon: Target,
            label: 'War Room',
            isGroup: true,
            subItems: [
                { path: '/command-center', icon: Zap, label: 'Command Center' },
                { path: '/saved-funnels', icon: GitBranch, label: 'Funnels' },
                { path: '/campaigns', icon: TrendingUp, label: 'Campaigns' },
                { path: '/campaign-creatives', icon: Layers, label: 'Campaign Creatives' },
                { path: '/placement-insights', icon: PieChart, label: 'Placement Insights' },
                { path: '/creative-analyzer', icon: Search, label: 'Creative Analyzer' },
                { path: '/store-cro', icon: Store, label: 'Store CRO' },
            ]
        },
        { path: '/insights', icon: Lightbulb, label: 'AI Insights' },
        { path: '/automation', icon: Zap, label: 'Automation' },
        { path: '/team', icon: Users, label: 'Team' },
        { path: '/admin/flow', icon: Settings, label: 'Admin Flow' },
    ];

    const isLowBalance = creditBalance < 20000; // ~1 workflow remaining

    return (
        <>
            {/* Mobile Menu Button */}
            <button
                className="btn btn-icon mobile-menu-btn"
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                style={{
                    position: 'fixed',
                    top: 'var(--space-4)',
                    left: 'var(--space-4)',
                    zIndex: 'var(--z-fixed)',
                    display: 'none',
                }}
            >
                {isMobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Sidebar Navigation */}
            <nav
                className={`sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    width: isCollapsed ? '80px' : '260px',
                    background: 'var(--color-surface)',
                    borderRight: '1px solid var(--color-border)',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'width var(--transition-base)',
                    zIndex: 'var(--z-fixed)',
                }}
            >
                {/* Brand Selector */}
                <div
                    style={{
                        padding: 'var(--space-4)',
                        borderBottom: '1px solid var(--color-border-light)',
                        position: 'relative',
                    }}
                >
                    <button
                        onClick={() => !isCollapsed && setIsBrandDropdownOpen(!isBrandDropdownOpen)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-3)',
                            width: '100%',
                            background: 'transparent',
                            border: 'none',
                            padding: 'var(--space-2)',
                            borderRadius: 'var(--radius-lg)',
                            cursor: isCollapsed ? 'default' : 'pointer',
                            transition: 'background var(--transition-fast)',
                        }}
                        className="brand-selector-btn"
                    >
                        <div
                            style={{
                                width: '40px',
                                height: '40px',
                                background: selectedBrand?.visual?.primaryColor && selectedBrand?.visual?.secondaryColor
                                    ? `linear-gradient(135deg, ${selectedBrand.visual.primaryColor}, ${selectedBrand.visual.secondaryColor})`
                                    : 'var(--gradient-primary)',
                                borderRadius: 'var(--radius-lg)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 'var(--font-size-xl)',
                                flexShrink: 0,
                                overflow: 'hidden',
                                padding: 'var(--space-1)',
                            }}
                        >
                            {selectedBrand?.logo && (selectedBrand.logo.startsWith('http') || selectedBrand.logo.startsWith('data:')) ? (
                                <img
                                    src={selectedBrand.logo}
                                    alt={selectedBrand.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            ) : (
                                selectedBrand?.logo || '⚡'
                            )}
                        </div>

                        {!isCollapsed && (
                            <>
                                <div style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                                    <h1 style={{
                                        fontSize: 'var(--font-size-md)',
                                        fontWeight: 600,
                                        margin: 0,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        {selectedBrand?.name || 'Vitisiti'}
                                    </h1>
                                    <p style={{ fontSize: 'var(--font-size-xs)', margin: 0, color: 'var(--color-text-tertiary)' }}>
                                        {selectedBrand ? 'Switch Brand' : 'Select Brand'}
                                    </p>
                                </div>
                                <ChevronDown size={16} style={{
                                    transform: isBrandDropdownOpen ? 'rotate(180deg)' : 'none',
                                    transition: 'transform var(--transition-fast)',
                                    color: 'var(--color-text-tertiary)'
                                }} />
                            </>
                        )}
                    </button>

                    {/* Dropdown Menu */}
                    {!isCollapsed && isBrandDropdownOpen && (
                        <div
                            style={{
                                position: 'absolute',
                                top: 'calc(100% + var(--space-2))',
                                left: 'var(--space-4)',
                                right: 'var(--space-4)',
                                background: 'var(--color-surface)',
                                border: '1px solid var(--color-border)',
                                borderRadius: 'var(--radius-lg)',
                                boxShadow: 'var(--shadow-lg)',
                                zIndex: 'var(--z-popover)',
                                overflow: 'hidden',
                                maxHeight: '300px',
                                overflowY: 'auto',
                            }}
                        >
                            {brands.map(brand => (
                                <button
                                    key={brand.id}
                                    onClick={() => {
                                        setSelectedBrand(brand);
                                        setIsBrandDropdownOpen(false);
                                        navigate('/dashboard');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        width: '100%',
                                        padding: 'var(--space-3)',
                                        background: selectedBrand?.id === brand.id ? 'var(--color-surface-hover)' : 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'background var(--transition-fast)',
                                        borderBottom: '1px solid var(--color-border-light)',
                                    }}
                                    className="brand-option"
                                >
                                    <span style={{
                                        fontSize: 'var(--font-size-lg)',
                                        width: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: 'var(--radius-md)',
                                        overflow: 'hidden',
                                        background: 'var(--color-bg-secondary)',
                                        flexShrink: 0
                                    }}>
                                        {brand.logo && (brand.logo.startsWith('http') || brand.logo.startsWith('data:')) ? (
                                            <img
                                                src={brand.logo}
                                                alt={brand.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            />
                                        ) : (
                                            brand.logo || '⚡'
                                        )}
                                    </span>
                                    <span style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{brand.name}</span>
                                </button>
                            ))}
                            {brands.length === 0 && (
                                <div style={{ padding: 'var(--space-3)', color: 'var(--color-text-tertiary)', fontSize: 'var(--font-size-sm)' }}>
                                    No brands found
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Navigation Links */}
                <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-4) 0' }}>
                    {navItems.map((item, index) => (
                        item.isGroup ? (
                            <div key={item.label}>
                                {/* Group Header */}
                                <button
                                    onClick={() => setIsWarRoomOpen(!isWarRoomOpen)}
                                    className="nav-link nav-group-header"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        padding: 'var(--space-3) var(--space-6)',
                                        color: 'var(--color-text-secondary)',
                                        background: 'transparent',
                                        border: 'none',
                                        width: '100%',
                                        cursor: 'pointer',
                                        transition: 'all var(--transition-fast)',
                                        fontWeight: 500,
                                        textAlign: 'left',
                                        fontSize: 'inherit',
                                    }}
                                >
                                    <item.icon size={20} />
                                    {!isCollapsed && (
                                        <>
                                            <span style={{ flex: 1 }}>{item.label}</span>
                                            <ChevronRight
                                                size={16}
                                                style={{
                                                    transform: isWarRoomOpen ? 'rotate(90deg)' : 'none',
                                                    transition: 'transform var(--transition-fast)',
                                                }}
                                            />
                                        </>
                                    )}
                                </button>
                                {/* Sub Items */}
                                {isWarRoomOpen && (
                                    <div style={{
                                        overflow: 'hidden',
                                        background: 'var(--color-bg-secondary)',
                                    }}>
                                        {item.subItems.map((subItem) => (
                                            <NavLink
                                                key={subItem.path}
                                                to={subItem.path}
                                                className={({ isActive }) => `nav-link nav-sub-link ${isActive ? 'active' : ''}`}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--space-3)',
                                                    padding: 'var(--space-3) var(--space-6)',
                                                    paddingLeft: isCollapsed ? 'var(--space-6)' : 'var(--space-10)',
                                                    color: 'var(--color-text-secondary)',
                                                    textDecoration: 'none',
                                                    transition: 'all var(--transition-fast)',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                <subItem.icon size={18} />
                                                {!isCollapsed && <span>{subItem.label}</span>}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-3)',
                                    padding: 'var(--space-3) var(--space-6)',
                                    color: 'var(--color-text-secondary)',
                                    textDecoration: 'none',
                                    transition: 'all var(--transition-fast)',
                                    fontWeight: 500,
                                    position: 'relative',
                                }}
                            >
                                <span style={{ position: 'relative', display: 'inline-flex' }}>
                                    <item.icon size={20} />
                                    {item.badge && (
                                        <span style={{
                                            position: 'absolute',
                                            top: '-4px',
                                            right: '-6px',
                                            minWidth: '16px',
                                            height: '16px',
                                            padding: '0 4px',
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            lineHeight: '16px',
                                            textAlign: 'center',
                                            color: 'white',
                                            background: 'var(--color-primary)',
                                            borderRadius: '8px',
                                        }}>
                                            {item.badge > 99 ? '99+' : item.badge}
                                        </span>
                                    )}
                                </span>
                                {!isCollapsed && (
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                        {item.label}
                                    </span>
                                )}
                            </NavLink>
                        )
                    ))}
                </div>

                {/* Bottom Section */}
                <div
                    style={{
                        padding: 'var(--space-4)',
                        borderTop: '1px solid var(--color-border-light)',
                    }}
                >
                    {/* Notifications Widget */}
                    {!isCollapsed && (
                        <div style={{ marginBottom: 'var(--space-3)' }}>
                            <SidebarNotifications />
                        </div>
                    )}

                    {/* Credit Balance */}
                    {!isCollapsed && (
                        <div
                            style={{
                                marginBottom: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: isLowBalance ? 'rgba(251, 191, 36, 0.1)' : 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                border: isLowBalance ? '1px solid rgba(251, 191, 36, 0.3)' : 'none',
                                cursor: 'pointer',
                                transition: 'all var(--transition-fast)',
                            }}
                            onClick={() => navigate('/billing')}
                            className="credit-balance-card"
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                marginBottom: 'var(--space-2)',
                            }}>
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em'
                                }}>
                                    Credits
                                </span>
                                <span style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-text-tertiary)'
                                }}>
                                    ≈ ${creditsToUSD(creditBalance)}
                                </span>
                            </div>
                            <div style={{
                                fontSize: 'var(--font-size-lg)',
                                fontWeight: 700,
                                color: isLowBalance ? 'rgb(251, 191, 36)' : 'var(--color-primary)',
                                marginBottom: 'var(--space-2)',
                            }}>
                                {formatCredits(creditBalance)}
                            </div>
                            {isLowBalance && (
                                <div style={{
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'rgb(251, 191, 36)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-1)',
                                }}>
                                    ⚠️ Low balance - Add credits
                                </div>
                            )}
                            {!isLowBalance && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate('/billing');
                                    }}
                                    className="btn btn-sm btn-primary"
                                    style={{
                                        width: '100%',
                                        marginTop: 'var(--space-2)',
                                        fontSize: 'var(--font-size-xs)'
                                    }}
                                >
                                    Add Credits
                                </button>
                            )}
                        </div>
                    )}

                    {/* Theme Toggle */}
                    <button
                        onClick={toggleTheme}
                        className="btn btn-ghost"
                        style={{ width: '100%', marginBottom: 'var(--space-3)' }}
                    >
                        {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
                        {!isCollapsed && <span>{theme === 'dark' ? 'Light Mode' : 'Dark Mode'}</span>}
                    </button>

                    {/* User Profile */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => !isCollapsed && setIsUserMenuOpen(!isUserMenuOpen)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-3)',
                                padding: 'var(--space-3)',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-lg)',
                                border: 'none',
                                cursor: isCollapsed ? 'default' : 'pointer',
                                width: '100%',
                                textAlign: 'left',
                                transition: 'background var(--transition-fast)',
                            }}
                            className="user-menu-btn"
                        >
                            <div className="avatar avatar-sm">
                                {user.avatar ? <img src={user.avatar} alt={user.name} /> : getInitials(user.name)}
                            </div>
                            {!isCollapsed && (
                                <>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                                            {user.name}
                                        </div>
                                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                                            {user.role}
                                        </div>
                                    </div>
                                    <ChevronDown size={16} style={{
                                        transform: isUserMenuOpen ? 'rotate(180deg)' : 'none',
                                        transition: 'transform var(--transition-fast)',
                                        color: 'var(--color-text-tertiary)'
                                    }} />
                                </>
                            )}
                        </button>

                        {/* User Dropdown Menu */}
                        {!isCollapsed && isUserMenuOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: 'calc(100% + var(--space-2))',
                                    left: 0,
                                    right: 0,
                                    background: 'var(--color-surface)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-lg)',
                                    boxShadow: 'var(--shadow-lg)',
                                    zIndex: 'var(--z-popover)',
                                    overflow: 'hidden',
                                }}
                            >
                                <button
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        navigate('/account');
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        width: '100%',
                                        padding: 'var(--space-3)',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'background var(--transition-fast)',
                                        color: 'var(--color-text-primary)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                        borderBottom: '1px solid var(--color-border-light)',
                                    }}
                                    className="user-menu-item"
                                >
                                    <User size={18} />
                                    <span>Account Settings</span>
                                </button>
                                <button
                                    onClick={() => {
                                        setIsUserMenuOpen(false);
                                        handleSignOut();
                                    }}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-3)',
                                        width: '100%',
                                        padding: 'var(--space-3)',
                                        background: 'transparent',
                                        border: 'none',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'background var(--transition-fast)',
                                        color: 'var(--color-error)',
                                        fontSize: 'var(--font-size-sm)',
                                        fontWeight: 500,
                                    }}
                                    className="user-menu-item"
                                >
                                    <LogOut size={18} />
                                    <span>Sign Out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </nav>

            <style>{`
        .nav-link:hover {
          background: var(--color-surface-hover);
          color: var(--color-text-primary);
        }

        .nav-link.active {
          background: linear-gradient(90deg,
            rgba(124, 58, 237, 0.1) 0%,
            transparent 100%);
          color: var(--color-primary);
          border-left: 3px solid var(--color-primary);
        }

        .nav-group-header:hover {
          background: var(--color-surface-hover);
          color: var(--color-text-primary);
        }

        .nav-sub-link {
          border-left: 2px solid transparent;
        }

        .nav-sub-link.active {
          border-left: 2px solid var(--color-primary);
        }

        .brand-selector-btn:hover,
        .brand-option:hover {
            background: var(--color-surface-hover) !important;
        }

        .user-menu-btn:hover {
            background: var(--color-surface-hover) !important;
        }

        .user-menu-item:hover {
            background: var(--color-surface-hover) !important;
        }

        @media (max-width: 768px) {
          .mobile-menu-btn {
            display: flex !important;
          }

          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.mobile-open {
            transform: translateX(0);
          }
        }
      `}</style>
        </>
    );
};

export default Navigation;
